// Angular components
import {
  AfterViewInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  effect,
  inject,
  signal,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Inject, PLATFORM_ID } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Subject, Subscription } from "rxjs";

// Self-made components
import { SidenavService } from '@app/core/layout/sidenav.service';
import { StoreUserService } from '@app/core/state/store-user.service';
import {
  DatabaseCacheService,
  Breadcrumb,
} from "../../../data/cache/database-cache.service";
import { RecordData } from "../../../types";
import {
  MeasuredDataDetailsState,
  MeasuredDataDetailsViewModel,
} from "./measured-data-details.viewmodel";
import { adjustStreamingUrlForBrowser } from "./measured-data-details-media.helper";
import { MeasuredDataDetailsWaveService } from "./measured-data-details-wave.service";
import {
  CsvProcessingContext,
  processCsvResults,
} from "./measured-data-details-csv.helper";
import { RecordingEvent, MarkerWithLabel, SeekEvent } from "./measured-data-details.types";
import { MeasuredDataDetailsRegionService } from "./measured-data-details-region.service";
import { PlayerControllerService, PlayerSnapshot } from "./services/player-controller.service";
import { MeasuredDataSummaryCardComponent } from "./components/summary-card/measured-data-summary-card.component";
import { MeasuredDataNeckAngleCardComponent } from "./components/neck-angle-card/measured-data-neck-angle-card.component";
import { MeasuredDataWaveCardComponent } from "./components/wave-card/measured-data-wave-card.component";
import { ProtocolRsstComponent } from "./components/protocol-rsst/protocol-rsst.component";
import { ProtocolMwstComponent } from "./components/protocol-mwst/protocol-mwst.component";
import { ProtocolVsComponent } from "./components/protocol-vs/protocol-vs.component";
import { buildParsedMeasurement, ParsedMeasurement, PostureDataPoint } from "./models/parsed-measurement.model";
import { WaveTimelineService } from "./services/wave-timeline.service";
import {
  NeckAngleService,
  NeckOrientation,
} from "./services/neck-angle.service";
import { ResourceCacheService } from "./services/resource-cache.service";
import { AvSyncService } from "./services/av-sync.service";

// 3rd party components
import { EChartsOption } from "echarts";
import * as echarts from "echarts";
import { Papa } from "ngx-papaparse";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import duration from "dayjs/plugin/duration";

import { GostUtils } from "../cards/recordings/gost-utils";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { MEASURED_DATA_DETAILS_I18N } from "./measured-data-details.i18n";
import { PopupWindowStateService } from "../expert-dialog/popup-window/popup-window-state.service";
import { GenericRecordingComponent } from "../cards/recordings/generic-recording.component";
import { VitalRecordingComponent } from "../cards/recordings/vital-recording.component";
import { GostRecordingComponent } from "../cards/recordings/gost-recording.component";
import { GostInfosComponent } from "../cards/recordings/gost-infos.component";
import { GostDigestComponent } from "../cards/recordings/gost-digest.component";
import { MwstFtGRecordingComponent } from "../cards/recordings/mwstftg-recording.component";
import { ProtocolSstpComponent } from "./components/protocol-sstp/protocol-sstp.component";
import { ProtocolTomassComponent } from "./components/protocol-tomass/protocol-tomass.component";
import { ProtocolMonitoringComponent } from "./components/protocol-monitoring/protocol-monitoring.component";
import { EventTableComponent } from "./components/shared/event-table/event-table.component";
import { DataFooterComponent } from "./components/shared/data-footer/data-footer.component";
import { NoRecordingComponent } from "../cards/recordings/no-recording.component";
import { tr } from "date-fns/locale";

//import { MKPlayer } from '@mediakind/mkplayer'; BUILDが失敗するため直接JSを利用
declare var mkplayer: any;

@Component({
  selector: "app-measured-data-details",
  templateUrl: "./measured-data-details.component.html",
  styleUrls: ["./measured-data-details.component.scss"],
  providers: [GostUtils, MeasuredDataDetailsViewModel, PlayerControllerService, AvSyncService],
  host: {
    '[class.host--gost]': 'recordtype === 7',
  },
  imports: [
    ...SHARED_IMPORTS,
    VitalRecordingComponent,
    GostRecordingComponent,
    GostInfosComponent,
    MwstFtGRecordingComponent,
    NoRecordingComponent,
    MeasuredDataSummaryCardComponent,
    MeasuredDataNeckAngleCardComponent,
    MeasuredDataWaveCardComponent,
    ProtocolRsstComponent,
    ProtocolMwstComponent,
    ProtocolVsComponent,
    ProtocolSstpComponent,
    ProtocolTomassComponent,
    ProtocolMonitoringComponent,
    EventTableComponent,
    DataFooterComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasuredDataDetailsComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  protected readonly i18n = MEASURED_DATA_DETAILS_I18N;
  private readonly destroyRef = inject(DestroyRef);
  //パンくずリスト
  breadcrumbs: Breadcrumb[];

  private toolbarTitle = MEASURED_DATA_DETAILS_I18N.toolbar.title;
  selectedUserWithUserData;

  @Input() url: string;
  @Input() waveColor = "#B5BEC8";
  @Input() progressColor = "#00AEEF";
  @Input() cursorColor = "#CCCCCC";

  error: any;
  recordId = "";
  pieChartData: Array<number> = [];
  pieChartColor: Array<string> = [];

  selectedSection;
  selectedUserId;

  events: RecordingEvent[] = [];
  parsedMeasurement: ParsedMeasurement | null = null;
  scyncedSeek = false;

  selectedEvent: RecordingEvent = null;
  waveLoadingPercent: number | null = null;
  waveProcessing = false;
  waveDownloading = false;
  waveDownloadPercent: number | null = null;
  waveErrorMessage: string | null = null;
  private waveInitRequested = false;
  private waveInitInProgress = false;
  private pendingWaveInit = false;
  private waveInitRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private waveInitRetryAttempt = 0;
  private readonly waveInitRetryMaxAttempts = 400;
  private waveReadyPending = false;
  private waveReadyTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  // 「無進捗ウォッチドッグ」: 固定の総タイムアウトではなく、進捗（DL/loading/processing）が
  // 来るたびにタイマーをリセットし、この秒数だけ進捗が途絶えたときに初めて失敗扱いにする。
  // - 低速だが進捗している転送は false 検知しない（チャンクごとにリセット）
  // - ストリーミングは ready が即時のため、ヘッダ取得が止まった場合のみ素早く検知
  // - ファイルサイズ・バイトレートに依存しない（duration はヘッダ実値を使う設計と整合）
  private readonly waveProgressTimeoutMs = 30000;
  private recordLoadTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly recordLoadTimeoutMs = 20000;
  private recordTypeUpdateTimer: ReturnType<typeof setTimeout> | null = null;
  private waveCallbackSession = 0;

  markerZOrder = 1;

  recordDataUpdated: Subject<any> = new Subject();
  private vmSubscription = new Subscription();

  private waveCardComponent?: MeasuredDataWaveCardComponent;
  @ViewChild(MeasuredDataWaveCardComponent)
  set waveCard(component: MeasuredDataWaveCardComponent | undefined) {
    const previous = this.waveCardComponent;
    this.waveCardComponent = component;
    // コンテナが切り替わった場合（例: recordtype 変更で汎用カード → タイムラインカード）、
    // 旧コンテナの WaveSurfer を破棄して再初期化を許可する。
    // GOSTのタイムライン折りたたみで一度 component が undefined になった場合も
    // 次回展開時に再初期化できるよう状態を必ずリセットする。
    const containerSwitched =
      (component && previous && component !== previous)
      || (!component && !!previous);
    if (
      containerSwitched &&
      (this.waveInitialized || this.waveInitRequested || this.waveInitInProgress || this.pendingWaveInit)
    ) {
      this.waveService.destroy();
      this.waveInitialized = false;
      this.waveInitInProgress = false;
      this.waveInitRequested = false;
      this.pendingWaveInit = false;
      this.waveReadyPending = false;
      this.clearWaveReadyTimeout();
      this.clearWaveInitRetryTimer();
      this.playing = false;
    }
    this.requestWaveInitIfNeeded();
  }

  recordtype: number = -1;
  private readonly popupWindowState = inject(PopupWindowStateService);
  protected readonly avSync = inject(AvSyncService);

  // ── PlayerController snapshot → ローカルプロパティ同期 ──
  // テンプレートと波形同期コードの後方互換のためローカルプロパティを維持
  videoMuted = false;
  videoCurrentTime = 0;
  displayVideoCurrentTime = 0;
  videoDuration = 0;
  displayVideoDuration = 0;
  videoPlaybackRate = 1;
  playbackEnded = false;
  videoPlaying = false;
  videoLoadFailed = false;
  videoReady = false;

  private encodingPollTimer: ReturnType<typeof setTimeout> | null = null;
  private eventSeekStopTimer: ReturnType<typeof setTimeout> | null = null;
  // 動画-波形の同期フラグ
  private avSyncEnabled = true;
  private destroyed = false;
  private readonly regionService = new MeasuredDataDetailsRegionService();
  progress = 0;
  playing = false;
  // 波形→動画シーク時の再開制御用
  private seekInProgress = false;
  private wasPlayingBeforeSeek = false;
  private seekInProgressTimer: any = null;
  // 動画へのシーク命令デバウンス（連打・低スペック対策）
  private seekDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  // WAV-master: 並走中の動画ドリフト補正スロットリング。
  private lastVideoDriftCheckMs = 0;
  private readonly VIDEO_DRIFT_THRESHOLD_SEC = 0.15;
  private readonly VIDEO_DRIFT_CHECK_INTERVAL_MS = 300;
  // MediaKind の終端付近シーク(2秒巻き戻し)を避けるため、末尾この秒数以内へはシークしない。
  private readonly VIDEO_TAIL_GUARD_SEC = 2.5;
  // ユーザーが明示的に停止を要求したフラグ。
  // effect が videoPlaying=true に戻す前に onWaveSeeking が再発火しても seekInProgress を再セットしない。
  // Paused イベント確定後（syncFromPlayerSnapshot の pause block）にリセットする。
  private stopRequested = false;
  // 終了処理中に再生イベントを無視するためのフラグ
  private finishingReset = false;
  // 波形側シーク時に発火する一時的なポーズイベントを無視するためのフラグ
  private ignoreNextPause = false;
  private userWaveSeek = false;
  private initialSeekIgnored = false;
  private prevPlayerSnapshot: PlayerSnapshot | null = null;
  public recordData: RecordData;
  public mediaRecordData: RecordData;
  // 同一メディアの重複 handleMediaRecordData を弾くための直近参照（初期化チャーン削減）。
  private lastHandledMediaRef: any = null;
  public recordDataContainer = { recordData: {} };
  public vsMediaFallbackData: any | null = null;
  /**
   * getMedia が失敗（500等）し再生用フォールバックも無い場合に、
   * 動画カードへエラー表示をさせるための合成データ。
   */
  public readonly mediaErrorFallbackData = {
    data: { mediaType: "Video", mediaStatus: "Error" },
  };
  public mediaLoadFailed = false;
  private lastMediaError: string | null = null;
  private consecutiveMediaLoadErrors = 0;
  /** recordWithData の recordData.id が null 等でメディアドキュメントIDが特定できない状態 */
  private mediaIdUnavailable = false;

  recordLoadingSuccess = true;
  isLoadingRecord = false;
  showRsstChart = false;
  showMwstView = false;
  showMonitoringView = false;
  showWaveCard = false;
  gostTimelineExpanded = false;
  private waveInitialized = false;
  private waveDurationSec = 0; // 見かけの波形長（WAV-master では raw 波形長）
  private waveDurationRawSec = 0; // 生の波形長

  /**
   * 動画カバー範囲の頭側（VSS手前）の幅比率（0..1）。
   * raw 波形長に対する `videoCoversStartSec` の割合。波形左端の overlay 用。
   * WAV-master では再生不可領域ではなく「動画が存在しない区間」を示す情報表示。
   */
  get avSyncFrontTrimRatio(): number {
    const w = this.avSync.window();
    if (!w.enabled || this.waveDurationRawSec <= 0) return 0;
    return Math.max(0, Math.min(1, w.videoCoversStartSec / this.waveDurationRawSec));
  }

  /**
   * 動画カバー範囲の尾側（VSE以降）の幅比率（0..1）。波形右端の overlay 用。
   */
  get avSyncEndTrimRatio(): number {
    const w = this.avSync.window();
    if (!w.enabled || this.waveDurationRawSec <= 0) return 0;
    const tail = this.waveDurationRawSec - w.videoCoversEndSec;
    return Math.max(0, Math.min(1, tail / this.waveDurationRawSec));
  }

  beforeMealImage;
  afterMealImage;

  zoomValue = 0;
  zoomStep = 0;

  timelineOffsetReference = 0;
  currentStartOffset = 0;

  @ViewChild("gost_infos_card") gostInfosCardCmp?: GostInfosComponent;
  @ViewChild("gost_infos_card", { read: ElementRef })
  gostInfosCardEl?: ElementRef<HTMLElement>;

  private onWavePlay(): void {
    // 波形(WAV master)からの再生開始をUIに反映
    this.playing = true;
    this.playbackEnded = false;
    // WAV-master: ミュート動画を現在の wave 位置へ即時合わせて並走開始させる。
    if (this.avSync.window().enabled) {
      this.playerController.setMuted(true);
      const ws = this.getActiveWaveSurfer();
      const t = Number(ws?.getCurrentTime?.() ?? 0);
      this.followVideoToWave(t, { hardSeek: true });
    }
    this.cdr.markForCheck();
  }

  private getActiveWaveSurfer() {
    return this.waveService.getWaveSurfer();
  }

  /**
   * VS(WAV-master) 時の WAV 実ミュート状態。動画は恒久ミュートのため、ミュートUIの
   * アイコンはこの値を反映する。onWaveReady で WAV 実状態に同期し、トグルで更新する。
   */
  readonly wavMuted = signal(false);

  /**
   * 動画カードのミュートアイコン表示用の上書き値。
   * VS(WAV-master) のときは WAV の実ミュート状態、それ以外は null（動画状態を使う）。
   */
  get vsAudioMutedForDisplay(): boolean | null {
    return this.isVsWaveMaster() ? this.wavMuted() : null;
  }

  /**
   * VS の WAV-master 状態か。true のとき WAV(wavesurfer) が音/クロックの master で、
   * ミュート動画はそれに従属する。
   */
  private isVsWaveMaster(): boolean {
    return this.avSync.window().enabled && !!this.getActiveWaveSurfer();
  }

  /** WAV(master) が終端付近にあるか。replay 判定の保険（finish 取りこぼし対策）。 */
  private isWaveAtEnd(): boolean {
    const ws: any = this.getActiveWaveSurfer();
    const dur = Number(ws?.getDuration?.() ?? 0);
    const cur = Number(ws?.getCurrentTime?.() ?? 0);
    return dur > 0 && cur >= dur - 0.05;
  }

  /**
   * WAV(master) の現在位置 `waveTime`(raw wave 秒) に合わせて、ミュート動画を従属させる。
   * - カバー範囲内: 動画時刻 = waveToVideo(waveTime)。hardSeek 時はシーク、それ以外は
   *   閾値超え時のみドリフト補正。WAV 再生中なら動画も再生。
   * - カバー範囲外(VSS手前/VSE以降): 動画を端フレームで pause（WAV は継続）。
   */
  private followVideoToWave(
    waveTime: number,
    opts: { hardSeek?: boolean; debounce?: boolean } = {},
  ): void {
    const w = this.avSync.window();
    if (!w.enabled || !this.playerController.isSessionActive()) {
      return;
    }
    if (!Number.isFinite(waveTime)) {
      return;
    }

    if (w.videoCovers(waveTime)) {
      const target = w.waveToVideo(waveTime);
      if (opts.hardSeek) {
        this.seekVideo(target, opts.debounce ?? false);
      } else {
        this.maybeCorrectVideoDrift(target);
      }
      if (this.playing && !this.playerController.snapshot().playing) {
        this.playerController.play();
      }
      return;
    }

    // カバー範囲外: 動画は停止、WAV は鳴り続ける。
    if (this.playerController.snapshot().playing) {
      this.playerController.pause();
    }
    if (waveTime < w.videoCoversStartSec) {
      // 頭側(VSS手前): ユーザー操作時のみ先頭フレームへ。
      if (opts.hardSeek) {
        this.seekVideo(0, opts.debounce ?? false);
      }
    }
    // 終端側(VSE以降): 動画は自然終了で最終フレームにいる。MediaKind の終端付近シーク
    // (2秒巻き戻し仕様)を誘発しないため、ここでは seek しない。スクラバーの 100% 表示は
    // video-card 側がマスター終了(playbackEnded)時に補完する。
  }

  private seekVideo(videoTime: number, debounce: boolean): void {
    if (debounce) {
      if (this.seekDebounceTimer) {
        clearTimeout(this.seekDebounceTimer);
      }
      this.seekDebounceTimer = setTimeout(() => {
        this.seekDebounceTimer = null;
        this.playerController.seek(videoTime);
      }, 80);
      return;
    }
    if (this.seekDebounceTimer) {
      clearTimeout(this.seekDebounceTimer);
      this.seekDebounceTimer = null;
    }
    this.playerController.seek(videoTime);
  }

  private maybeCorrectVideoDrift(targetVideo: number): void {
    // MediaKind は終端付近(約2秒)へのシークで必ず2秒巻き戻してから再生する仕様のため、
    // 終端ゾーンでは補正シークしない（動画は自然終了で最終フレームへ到達させる）。
    const videoDuration = this.avSync.window().videoDurationSec;
    if (videoDuration > 0 && targetVideo > videoDuration - this.VIDEO_TAIL_GUARD_SEC) {
      return;
    }
    const now =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (now - this.lastVideoDriftCheckMs < this.VIDEO_DRIFT_CHECK_INTERVAL_MS) {
      return;
    }
    this.lastVideoDriftCheckMs = now;
    const cur = Number(
      this.playerController.getMkplayerInstance()?.getCurrentTime?.() ?? Number.NaN,
    );
    if (!Number.isFinite(cur)) {
      return;
    }
    if (Math.abs(cur - targetVideo) > this.VIDEO_DRIFT_THRESHOLD_SEC) {
      this.playerController.seek(targetVideo);
    }
  }

  private runWaveCallback(
    callback: () => void,
    options: { defer?: boolean; session?: number } = {},
  ): void {
    const { defer = false, session } = options;
    const invoke = () => {
      if (this.destroyed) {
        return;
      }
      if (session !== undefined && session !== this.waveCallbackSession) {
        return;
      }
      callback();
      if (!this.destroyed) {
        this.cdr.markForCheck();
      }
    };

    if (defer) {
      this.ngZone.runOutsideAngular(() => {
        queueMicrotask(() => {
          if (this.destroyed) {
            return;
          }
          this.ngZone.run(invoke);
        });
      });
      return;
    }

    if (NgZone.isInAngularZone()) {
      invoke();
      return;
    }
    this.ngZone.run(invoke);
  }

  private clearRecordTypeUpdateTimer(): void {
    if (this.recordTypeUpdateTimer) {
      clearTimeout(this.recordTypeUpdateTimer);
      this.recordTypeUpdateTimer = null;
    }
  }

  private isWaveContainerReady(
    container: HTMLElement | null | undefined,
  ): container is HTMLElement {
    if (!container || !container.isConnected) {
      return false;
    }
    return this.getWaveContainerWidth(container) > 0;
  }

  private getWaveContainerWidth(
    container: HTMLElement | null | undefined,
  ): number {
    if (!container) {
      return 0;
    }
    const rectWidth = container.getBoundingClientRect?.().width ?? 0;
    return container.clientWidth || container.offsetWidth || rectWidth || 0;
  }

  private clearWaveInitRetryTimer(resetAttempt = true): void {
    if (this.waveInitRetryTimer) {
      clearTimeout(this.waveInitRetryTimer);
      this.waveInitRetryTimer = null;
    }
    if (resetAttempt) {
      this.waveInitRetryAttempt = 0;
    }
  }

  private clearWaveReadyTimeout(): void {
    if (this.waveReadyTimeoutTimer) {
      clearTimeout(this.waveReadyTimeoutTimer);
      this.waveReadyTimeoutTimer = null;
    }
  }

  private startWaveReadyTimeout(): void {
    this.waveReadyPending = true;
    this.armWaveReadyTimer();
  }

  /** 無進捗ウォッチドッグのタイマーを張り直す（既存タイマーはクリア）。 */
  private armWaveReadyTimer(): void {
    this.clearWaveReadyTimeout();
    this.ngZone.runOutsideAngular(() => {
      this.waveReadyTimeoutTimer = setTimeout(() => {
        this.ngZone.run(() => {
          if (!this.waveReadyPending || this.destroyed) {
            return;
          }
          this.onWaveError(new Error("WAVE_READY_TIMEOUT"));
        });
      }, this.waveProgressTimeoutMs);
    });
  }

  /**
   * 進捗イベント（DL/loading/processing）受信時にウォッチドッグをリセットする。
   * これにより「遅いだけ」の転送を失敗扱いにせず、本当に途絶えたときだけ検知する。
   */
  private bumpWaveReadyWatchdog(): void {
    if (this.waveReadyPending) {
      this.armWaveReadyTimer();
    }
  }

  private clearRecordLoadTimeout(): void {
    if (this.recordLoadTimeoutTimer) {
      clearTimeout(this.recordLoadTimeoutTimer);
      this.recordLoadTimeoutTimer = null;
    }
  }

  private startRecordLoadTimeout(): void {
    this.clearRecordLoadTimeout();
    this.ngZone.runOutsideAngular(() => {
      this.recordLoadTimeoutTimer = setTimeout(() => {
        this.ngZone.run(() => {
          if (!this.isLoadingRecord || this.destroyed) {
            return;
          }
          this.isLoadingRecord = false;
          this.recordLoadingSuccess = false;
          this.waveErrorMessage = this.i18n.waveError.audioFetchFailed;
          this.cdr.markForCheck();
        });
      }, this.recordLoadTimeoutMs);
    });
  }

  isMwstFoodTestRecord(): boolean {
    const type = this.recordData?.data?.recordType;
    return [
      "MWST",
      "MWST_G",
      "MWST_G_DATA",
      "FOOD_TEST",
      "FOOD_TEST_G",
      "FOOD_TEST_G_DATA",
    ].includes(type);
  }

  onWaveUserSeekStart(): void {
    this.userWaveSeek = true;
  }

  onWaveUserSeekEnd(): void {
    // ユーザー操作終了後に反映のみ有効化し、通常時の自動シークは無効
    setTimeout(() => (this.userWaveSeek = false), 0);
  }

  private onWavePause(): void {
    this.playing = false;
    // WAV-master: WAV が止まったら従属動画も止める。
    if (this.avSync.window().enabled) {
      this.playerController.pause();
    }
    this.cdr.markForCheck();
  }

  private onWaveFinish(): void {
    // WAV(master)が終端 = 全体の再生終了。
    // 終了/replay 判定とスクラバー100%表示はマスター(this.playbackEnded)を権威にする。
    // MediaKind の終端付近シーク(2秒巻き戻し)仕様を誘発しないよう、動画は末尾へシークしない。
    this.stopAllPlayback("wave-finish", true);
  }

  scrollToGostSection(section: "fils" | "sc" | "gc" | "oc") {
    const target = section === "fils" ? "sc" : section;
    if (this.gostInfosCardCmp?.onSummaryCardClick) {
      this.gostInfosCardCmp.onSummaryCardClick(target as any);
      return;
    }
    this.gostInfosCardEl?.nativeElement?.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  }

  private syncPlaybackRateFromVideo(): void {
    try {
      const rate = this.playerController.getMkplayerInstance()?.getPlaybackRate?.() ?? 1;
      this.videoPlaybackRate = rate;
      this.waveService.setPlaybackRate(rate);
    } catch {
      // ignore
    }
  }

  private onWaveAudioProcess(): void {
    const ws = this.getActiveWaveSurfer();
    const waveTime = Number(ws?.getCurrentTime?.() ?? 0);
    // neck angle / CSV 由来データは wave 時刻基準。
    this.findClosestNeckAngleValues(waveTime);
    // WAV-master: 並走中はドリフト閾値超え時のみ動画を補正シーク。
    if (this.avSync.window().enabled) {
      this.followVideoToWave(waveTime);
    }
  }

  private onWaveReady(): void {
    this.waveReadyPending = false;
    this.clearWaveReadyTimeout();
    const wavesurfer = this.getActiveWaveSurfer();
    if (!wavesurfer) {
      return;
    }
    // WAV 実ミュート状態をUIに同期（レコード切替で wavesurfer 再生成時の初期化を含む）。
    this.wavMuted.set(!!(wavesurfer as any).getMuted?.());
    const rawWaveDuration = Number(wavesurfer.getDuration?.() ?? 0) || 0;
    this.waveDurationRawSec = rawWaveDuration;
    // AvSync フォールバック: wavDuration メタデータが無い（将来の廃止含む）場合、
    // ヘッダ由来の実 duration を AvSync に流して前方トリム/同期窓を維持する。
    // メタデータがある場合は handleRecordDataLoaded で設定済みなので上書きしない。
    const wavDurationMetaMs = Number(this.recordData?.data?.wavDuration ?? 0);
    if (!(wavDurationMetaMs > 0) && rawWaveDuration > 0) {
      this.avSync.setWaveDuration(rawWaveDuration);
    }
    // WAV-master: 見かけの波形長は raw 波形長そのもの（動画尺でクランプしない）。
    this.waveDurationSec = rawWaveDuration;
    // duration logging is now handled by PlayerController
    this.waveLoadingPercent = null;
    // WAV-master: 波形・音・タグは raw wave 軸そのまま。初期位置は録音開始オフセット
    // (currentStartOffset) のみ。動画カバー範囲(VSS)へ前倒しはしない（頭側の WAV も再生可能）。
    const initialWaveTime = this.currentStartOffset;
    const initialDuration = wavesurfer.getDuration() || 1;
    wavesurfer.seekTo(initialWaveTime / initialDuration);

    this.zoomValue = this.waveService.resetZoom(this.zoomValue);
    this.waveService.refreshTimelineOffset();
    this.applyAvSyncWindowToWave();

    const duration = wavesurfer.getDuration();
    this.zoomStep = this.waveTimeline.computeZoomStep(duration);
    this.cdr.markForCheck();

    // regions プラグイン初期化完了後に CSV マーカーを再描画
    // loadCsvData は handleRecordDataLoaded でも呼ばれるが、その時点では
    // regions 未初期化のためマーカーが無視される
    void this.loadCsvData();
  }

  private onWaveError(error: any): void {
    this.hideWaveTimelineOnNotFound(error);
    this.waveReadyPending = false;
    this.clearWaveReadyTimeout();
    this.waveInitInProgress = false;
    this.waveInitRequested = false;
    this.pendingWaveInit = false;
    this.clearWaveInitRetryTimer();
    this.recordLoadingSuccess = false;
    this.waveErrorMessage = this.getWaveErrorMessage(error);
    this.waveLoadingPercent = null;
    this.waveProcessing = false;
    this.waveDownloading = false;
    this.waveDownloadPercent = null;
    this.cdr.markForCheck();
  }

  private onWaveLoading(percent: number): void {
    this.bumpWaveReadyWatchdog();
    this.waveLoadingPercent = Math.round(percent);
    this.cdr.markForCheck();
  }

  private onWaveProcessingStart(): void {
    this.bumpWaveReadyWatchdog();
    this.waveProcessing = true;
    this.waveLoadingPercent = null;
    this.cdr.markForCheck();
  }

  private onWaveProcessingDone(): void {
    this.waveProcessing = false;
    this.cdr.markForCheck();
  }

  private onWaveDownloadStart(): void {
    this.bumpWaveReadyWatchdog();
    this.waveDownloading = true;
    this.waveDownloadPercent = null;
    this.waveLoadingPercent = null;
    this.cdr.markForCheck();
  }

  private onWaveDownloadProgress(percent: number | null): void {
    this.bumpWaveReadyWatchdog();
    this.waveDownloadPercent = percent;
    this.cdr.markForCheck();
  }

  private onWaveDownloadDone(): void {
    this.waveDownloading = false;
    this.waveDownloadPercent = null;
    this.cdr.markForCheck();
  }

  private onWaveSeeking(currentTime: number): void {
    // currentTime は raw wave 時刻（= 波形・タグ・音と同一軸）。
    this.findClosestNeckAngleValues(currentTime);
    this.playbackEnded = false;

    // WAV-master: WAV が master。ミュート動画を wave 位置へ従属させる。
    // ドラッグ連打・低スペックPC での MKPlayer 過負荷を抑えるため debounce する。
    if (this.avSync.window().enabled) {
      this.followVideoToWave(currentTime, { hardSeek: true, debounce: true });
    }
  }

  private syncNeckAngleZoom(target: echarts.ECharts | null, params: any): void {
    this.neckAngleService.syncZoom(target, params);
  }

  neckAngleScatterData: any[] = [];
  currentNeckAngleScatterData: any = null;
  neckOrientationData: NeckOrientation[] = [];
  currentNeckOrientation: NeckOrientation | null = null;
  private neckAngleScatterChartInstance: echarts.ECharts | null = null;
  private currentNeckAngleScatterChartInstance: echarts.ECharts | null = null;
  private readonly onBaseDataZoom = (params: any) =>
    this.syncNeckAngleZoom(this.currentNeckAngleScatterChartInstance, params);
  private readonly onCurrentDataZoom = (params: any) =>
    this.syncNeckAngleZoom(this.neckAngleScatterChartInstance, params);

  chartOption: EChartsOption;
  currentChartOption: EChartsOption;
  private neckAngleOptionUpdatePending = false;

  constructor(
    public storeUser: StoreUserService,
    private router: Router,
    private route: ActivatedRoute,
    private ref: ChangeDetectorRef,
    private papa: Papa,
    private sidenavService: SidenavService,
    private changeDetector: ChangeDetectorRef,
    private common: DatabaseCacheService,
    private storeService: StoreUserService,
    private cdr: ChangeDetectorRef,
    private viewModel: MeasuredDataDetailsViewModel,
    private waveService: MeasuredDataDetailsWaveService,
    protected playerController: PlayerControllerService,
    private resourceCache: ResourceCacheService,
    private waveTimeline: WaveTimelineService,
    private neckAngleService: NeckAngleService,
    private injector: Injector,
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.chartOption = this.neckAngleService.createChartOption(
      this.neckAngleScatterData,
    );
    this.currentChartOption = this.neckAngleService.createCurrentChartOption(
      [],
    );

    dayjs.extend(duration);
    // var utc = require('dayjs/plugin/utc');
    dayjs.extend(utc);

    this.subscribeToViewModel();
    this.vmSubscription.add(
      this.waveTimeline.viewState$.subscribe(
        ({ currentStartOffset, timelineOffsetReference }) => {
          this.currentStartOffset = currentStartOffset;
          this.timelineOffsetReference = timelineOffsetReference;
          this.cdr.markForCheck();
        },
      ),
    );

    this.sidenavService.getSidenavObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((sidenav) => {

      if (sidenav != null) {
        sidenav.openedChange.subscribe(
          (observerOrNext?: any, error?: any, complete?: any) => {
            const wavesurfer = this.getActiveWaveSurfer();
            if (wavesurfer != null) {
              wavesurfer.zoom(this.zoomValue);
            }

            if (this.neckAngleScatterChartInstance != null) {
              this.neckAngleScatterChartInstance.resize();
              this.currentNeckAngleScatterChartInstance.resize();
            }
          },
        );
      }
    });

    // ── PlayerController snapshot → ローカルプロパティ同期 ──
    effect(() => {
      const snap = this.playerController.snapshot();
      this.syncFromPlayerSnapshot(snap);
    });

    // ── VS(WAV-master)では動画再生を followVideoToWave に一本化する ──
    // PlayerController 自身の canplay 自動再生を無効化し、二重駆動による飛び/巻き戻しを防ぐ。
    effect(() => {
      this.playerController.setExternallyDriven(this.avSync.window().enabled);
    });
  }

  /**
   * PlayerController の snapshot signal 変更時に呼ばれる。
   * ローカルプロパティを更新し、波形同期を行う。
   */
  private syncFromPlayerSnapshot(snap: PlayerSnapshot): void {
    const prev = this.prevPlayerSnapshot;
    this.prevPlayerSnapshot = snap;

    // WAV-master(VS+動画): 動画は従属。動画の play/pause/finish/currentTime は
    // wave 側（再生状態・カーソル・終了判定）を駆動してはならない。
    const waveMaster = this.avSync.window().enabled;

    // ローカルプロパティ同期（テンプレート後方互換）
    this.videoPlaying = snap.playing;
    this.videoCurrentTime = snap.currentTime;
    this.videoDuration = snap.duration;
    this.displayVideoCurrentTime = snap.displayCurrentTime;
    this.displayVideoDuration = snap.displayDuration;
    this.videoPlaybackRate = snap.playbackRate;
    this.videoMuted = snap.muted;
    // WAV-master では全体の終了判定は WAV のみ。動画が先に終端でも playbackEnded にしない。
    this.playbackEnded = waveMaster ? this.playbackEnded : snap.playbackEnded;
    this.videoReady = snap.state === 'ready';
    this.videoLoadFailed = snap.state === 'error';

    // --- 状態遷移ベースの副作用 ---
    if (!prev) {
      this.cdr.markForCheck();
      return;
    }

    if (!waveMaster) {
      // 再生開始
      if (snap.playing && !prev.playing) {
        this.playing = true;
        if (this.finishingReset) {
          this.finishingReset = false;
        }
        if (this.avSyncEnabled) {
          this.updateWaveCursorFromVideo(true);
        }
      }

      // 一時停止（終了以外）
      if (!snap.playing && prev.playing && !snap.playbackEnded) {
        // Paused 確定: stopRequested をリセット（MKPlayer がパウズを報告した時点で解除）
        this.stopRequested = false;
        if (!this.ignoreNextPause) {
          if (!this.seekInProgress) {
            this.playing = false;
            const wavesurfer = this.getActiveWaveSurfer();
            if (wavesurfer && this.avSyncEnabled) {
              try { wavesurfer.pause(); } catch { /* ignore */ }
            }
          }
        }
        this.ignoreNextPause = false;
      }

      // 再生終了
      if (snap.playbackEnded && !prev.playbackEnded) {
        this.stopAllPlayback('player-finish', true);
      }
    }

    // 倍速変化 → wave にも伝える
    if (snap.playbackRate !== prev.playbackRate) {
      this.waveService.setPlaybackRate(snap.playbackRate);
    }

    // Duration 変更 → AvSync へ動画 duration を流入し再同期
    if (snap.duration !== prev.duration && snap.duration > 0) {
      // loadedmetadata/durationchange の後着でも整合させる
      this.avSync.setVideoDuration(snap.duration);
      this.applyAvSyncWindowToWave();
    }

    if (waveMaster) {
      // WAV-master では動画の currentTime/seeked は wave を駆動しない。
      this.cdr.markForCheck();
      return;
    }

    const currentTimeChanged =
      Math.abs(snap.currentTime - prev.currentTime) > 0.001;

    // Seeked イベント完了を検出: 400ms フォールバックを待たずに即時 play() 再開する。
    // 遅いネットワーク・低スペックPCで seekInProgressTimer(400ms) が先に発火して
    // シーク途中から再生が始まる問題を解消する。
    if (snap.seekedVersion !== prev.seekedVersion) {
      if (this.seekInProgress && this.wasPlayingBeforeSeek && !snap.playbackEnded) {
        if (this.seekInProgressTimer) {
          clearTimeout(this.seekInProgressTimer);
          this.seekInProgressTimer = null;
        }
        this.seekInProgress = false;
        this.wasPlayingBeforeSeek = false;
        this.playerController.play();
      }
    }

    // 再生中、または停止中シーク後（currentTime変化時）の波形・姿勢追従
    if ((snap.playing || currentTimeChanged) && this.avSyncEnabled) {
      if (!this.seekInProgress && !this.seekDebounceTimer) {
        this.updateWaveCursorFromVideo(false, snap.currentTime);
      }
      this.findClosestNeckAngleValues(snap.currentTime);
    }

    this.cdr.markForCheck();
  }

  private applyAvSyncWindowToWave(): void {
    const window = this.avSync.window();

    // WAV-master: WAV/CSV 軸が唯一の真実。再生/可視領域を動画カバー範囲にクランプせず、
    // タイムラインも歪ませない（動画外の WAV 頭/尾も再生・可視のまま）。
    this.waveService.setVisibleEndSec(null);
    this.waveService.setAvSyncLowerBound(0);
    this.waveTimeline.setAvSyncTimeOffset(0);

    // 見かけの波形長は raw 波形長（動画尺でクランプしない）。
    if (this.waveDurationRawSec > 0) {
      this.waveDurationSec = this.waveDurationRawSec;
    }

    if (window.enabled) {
      // 動画は恒久ミュート（聞こえる音は WAV のみ）。
      this.playerController.setMuted(true);
      // duration/CSV 後着で offset が確定したとき、ミュート動画を現在の wave 位置へ再同期。
      const ws = this.getActiveWaveSurfer();
      const waveTime = Number(ws?.getCurrentTime?.() ?? 0);
      this.followVideoToWave(waveTime, { hardSeek: true });
    }
  }

  private subscribeToViewModel(): void {
    this.vmSubscription.add(
      this.viewModel.vm$.subscribe((state) => {
        this.syncViewModelLoadingState(state);

        if (!state.recordData) {
          this.handleRecordDataCleared(state);
        } else if (state.recordData !== this.recordData) {
          this.handleRecordDataLoaded(state);
        }

        const currentMediaRecordData = this.getCurrentMediaRecordData(
          state.mediaRecordData,
        );

        if (!currentMediaRecordData) {
          this.mediaRecordData = null;
        } else if (currentMediaRecordData !== this.mediaRecordData) {
          this.handleMediaRecordData(currentMediaRecordData);
        }

        // getMedia 失敗時・メディアID特定不能時はローディングを継続せずエラー表示へ切り替える
        // （mediaRecordData が取得できれば自動で解消される）
        this.lastMediaError = state.mediaError;
        this.mediaLoadFailed =
          (!!state.mediaError || this.mediaIdUnavailable) && !currentMediaRecordData;
      }),
    );
  }

  private syncViewModelLoadingState(state: MeasuredDataDetailsState): void {
    if (state.recordLoading !== this.isLoadingRecord) {
      this.isLoadingRecord = state.recordLoading;
    }

    this.recordLoadingSuccess = state.recordLoadingSuccess;
    if (state.recordLoading) {
      this.startRecordLoadTimeout();
    } else {
      this.clearRecordLoadTimeout();
      this.waveLoadingPercent = null;
      this.waveErrorMessage = null;
    }

    if (state.recordError) {
      this.recordLoadingSuccess = false;
      const err: any = state.recordError;
      this.waveErrorMessage = this.getWaveErrorMessage(err);
    }

    this.cdr.markForCheck();
  }

  private getWaveErrorMessage(err: any): string {
    const msg = typeof err === "string" ? err : err?.message;
    if (msg && typeof msg === "string") {
      const lower = msg.toLowerCase();
      if (this.isNotFoundBlobError(err)) {
        return this.i18n.waveError.audioNotFound;
      }
      if (lower.includes("decode audio data")) {
        return this.i18n.waveError.decodeError;
      }
    }
    return this.i18n.waveError.audioFetchFailed;
  }

  private getRecordErrorMessage(error: unknown): string {
    if (this.isRecordNotFoundOrDeletedError(error)) {
      return this.i18n.recordError.notFoundOrDeleted;
    }
    return this.i18n.recordError.fetchFailed;
  }

  private isRecordNotFoundOrDeletedError(error: unknown): boolean {
    const status = this.getErrorStatus(error);
    if (status === 404) {
      return true;
    }
    if (status === 400 && this.isRecordWithDataEndpointError(error)) {
      return true;
    }
    const message = this.getErrorMessage(error).toLowerCase();
    return (
      message.includes("not found")
      || message.includes("does not exist")
      || message.includes("record not found")
      || message.includes("recordwithdata")
    );
  }

  private getErrorStatus(error: unknown): number | null {
    const status = (error as { status?: unknown } | null)?.status;
    return typeof status === "number" ? status : null;
  }

  private getErrorMessage(error: unknown): string {
    const message = (error as { message?: unknown } | null)?.message;
    if (typeof message === "string") {
      return message;
    }
    return "";
  }

  private getErrorUrl(error: unknown): string {
    const url = (error as { url?: unknown } | null)?.url;
    if (typeof url === "string") {
      return url;
    }
    return "";
  }

  private isRecordWithDataEndpointError(error: unknown): boolean {
    return this.getErrorUrl(error).includes("/recordWithData/");
  }

  private isNotFoundBlobError(error: any): boolean {
    if (error?.status === 404) {
      return true;
    }
    const msg = typeof error === "string" ? error : error?.message;
    if (typeof msg !== "string") {
      return false;
    }
    const lower = msg.toLowerCase();
    return (
      lower.includes("404") ||
      lower.includes("blob does not exist") ||
      lower.includes("specified blob")
    );
  }

  private hideWaveTimelineOnNotFound(error: any): void {
    if (!this.showWaveCard || !this.isNotFoundBlobError(error)) {
      return;
    }
    this.showWaveCard = false;
    this.waveService.destroy();
    this.waveInitialized = false;
    this.waveInitRequested = false;
    this.waveInitInProgress = false;
    this.pendingWaveInit = false;
    this.waveReadyPending = false;
    this.clearWaveReadyTimeout();
    this.clearWaveInitRetryTimer();
  }

  private hasStorageBlobResource(url: unknown, size: unknown): boolean {
    if (size === null) {
      return false;
    }
    if (size !== undefined) {
      const numericSize = Number(size);
      if (Number.isFinite(numericSize)) {
        return numericSize > 0;
      }
    }
    return typeof url === "string" ? url.trim().length > 0 : !!url;
  }

  private hasZeroDuration(recordData: any): boolean {
    return recordData?.totalTime === "00:00:00" || Number(recordData?.wavDuration ?? 0) <= 0;
  }

  private isGostV2Record(recordData: any): boolean {
    const type = recordData?.recordType;
    const isGostLikeType =
      type === "GOST" || type === "GOST_DATA" || type === "GOST_DATA_2";
    return isGostLikeType && String(recordData?.version ?? "") === "2";
  }

  private normalizeMediaStatus(status: unknown): string {
    return typeof status === "string" ? status.trim().toLowerCase() : "";
  }

  private toVideoCardMediaStatus(status: unknown): string | null {
    const normalizedStatus = this.normalizeMediaStatus(status);
    switch (normalizedStatus) {
      case "deleted":
        return "Deleted";
      case "failed":
        return "Failed";
      case "error":
        return "Error";
      case "canceled":
      case "cancelled":
        return "Canceled";
      default:
        return null;
    }
  }

  private getCurrentMediaDocumentId(): string | null {
    const mediaDocumentId = (this.recordData as any)?._mediaDocumentId;
    return typeof mediaDocumentId === "string" && mediaDocumentId
      ? mediaDocumentId
      : null;
  }

  private getMediaDocumentIdentity(media: any): string | null {
    const mediaDocumentId = media?.__requestedMediaId ?? media?.id;
    return typeof mediaDocumentId === "string" && mediaDocumentId
      ? mediaDocumentId
      : null;
  }

  private getCurrentMediaRecordData(media: any): any | null {
    if (!media) {
      return null;
    }

    const expectedMediaDocumentId = this.getCurrentMediaDocumentId();
    if (!expectedMediaDocumentId) {
      return null;
    }

    return this.getMediaDocumentIdentity(media) === expectedMediaDocumentId
      ? media
      : null;
  }

  private isPlayableVideoMedia(media: any): boolean {
    return (
      this.normalizeMediaStatus(media?.data?.mediaStatus) === "encoded" &&
      !!media?.data?.storage?.streamingURL
    );
  }

  private buildVsMediaFallbackData(record: RecordData): any | null {
    const storage = record.data?.storage ?? null;
    const mediaStatus = this.toVideoCardMediaStatus(
      storage?.mediaStatus ?? record.data?.mediaStatus,
    );

    if (mediaStatus) {
      return {
        data: {
          mediaType: "Video",
          mediaStatus,
          comment: record.data?.comment ?? "",
          storage,
        },
      };
    }

    if (!storage?.streamingURL) {
      return null;
    }

    return {
      data: {
        mediaType: "Video",
        mediaStatus: "Encoded",
        comment: record.data?.comment ?? "",
        storage,
      },
    };
  }

  private handleRecordDataLoaded(state: MeasuredDataDetailsState): void {
    const record = state.recordData;
    if (!record) {
      return;
    }

    this.error = null;

    const previousRecordId = this.recordData?.id;
    const wasWaveInitialized = this.waveInitialized;

    this.recordData = record;
    this.recordDataUpdated.next(this.recordData);
    this.recordDataContainer.recordData = this.recordData;

    // AvSync: recordType + raw WAV duration をサービスに流入。
    // wavDuration は recordData メタデータの ms 値。秒に変換して渡す。
    // 動画 duration は loadedmetadata 経由で別途流入（onPlayerSnapshot）。
    const avSyncRecordType = record.data?.recordType ?? null;
    const wavDurationMs = Number(record.data?.wavDuration ?? 0);
    const wavDurationSec = wavDurationMs > 0 ? wavDurationMs / 1000 : null;
    if (previousRecordId !== record.id) {
      this.avSync.reset();
    }
    this.avSync.setRecordType(avSyncRecordType);
    this.avSync.setWaveDuration(wavDurationSec);
    this.showWaveCard = this.shouldDisplayWaveCard(
      this.recordData,
      state.recordType,
    );

    this.beforeMealImage = state.beforeMealImage;
    this.afterMealImage = state.afterMealImage;

    this.showRsstChart = state.showRsstChart;
    this.showMwstView = state.showMwstView;
    this.showMonitoringView = state.showMonitoringView;
    this.mediaRecordData = null;
    this.mediaIdUnavailable = false;
    this.scheduleRecordTypeUpdate(state.recordType);
    // 同じレコードでの状態更新では再初期化を抑止する
    this.waveInitialized =
      previousRecordId === record.id ? wasWaveInitialized : false;

    this.vsMediaFallbackData = this.buildVsMediaFallbackData(record);
    if (this.vsMediaFallbackData) {
      adjustStreamingUrlForBrowser({
        mediaRecordData: this.vsMediaFallbackData,
        platformId: this.platformId,
      });
    }
    if (state.recordType === 4 && !this.isPlayableVideoMedia(this.vsMediaFallbackData)) {
      this.playerController.destroyPlayer();
    }

    switch (state.recordType) {
      case 4: {
        // recordWithData に埋め込まれた streamingURL は古い/期限切れの場合があるため、
        // メディアドキュメントID が取れたときは getMedia で最新のメディア情報を取得する。
        // recordData.id が null で返るレコード（バックエンドのデータ異常）では、
        // レコードIDで /medias を呼ぶと 500 になるため getMedia をスキップし、
        // vsMediaFallbackData（recordWithData 埋め込みデータ）のみで再生する。
        const mediaDocId = (this.recordData as any)?._mediaDocumentId;
        if (mediaDocId) {
          this.viewModel.loadMedia(mediaDocId);
          this.startEncodingPoll(mediaDocId);
        } else if (!this.vsMediaFallbackData) {
          // メディアドキュメントID も再生用フォールバックも無い場合はエラー表示
          this.mediaIdUnavailable = true;
        }
        break;
      }
      default:
        break;
    }

    this.requestWaveInitIfNeeded();
    void this.loadCsvData();

  }

  private requestWaveInitIfNeeded(): void {
    if (this.recordTypeUpdateTimer) {
      this.pendingWaveInit = true;
      return;
    }

    if (
      !this.showWaveCard ||
      this.waveInitialized ||
      this.waveInitInProgress ||
      this.waveInitRequested
    ) {
      return;
    }
    const wasPending = this.pendingWaveInit;
    this.pendingWaveInit = true;
    if (!wasPending) {
      this.waveInitRetryAttempt = 0;
    }
    const containerReady = this.isWaveContainerReady(this.waveCardComponent?.waveContainer);
    if (containerReady) {
      this.pendingWaveInit = false;
      this.clearWaveInitRetryTimer();
      this.waveInitRequested = true;
      void this.initWaveSurfer();
      return;
    }
    if (this.waveCardComponent) {
      this.retryWaveInitUntilReady();
    }
  }

  private handleRecordDataCleared(state: MeasuredDataDetailsState): void {
    const needsCleanup =
      !!this.recordData || !!this.showWaveCard || !!this.mediaRecordData;
    if (needsCleanup) {
      this.waveService.destroy();
      this.waveTimeline.reset();
    }
    this.waveInitialized = false;
    this.waveInitRequested = false;
    this.waveInitInProgress = false;
    this.clearWaveInitRetryTimer();
    this.waveReadyPending = false;
    this.clearWaveReadyTimeout();
    this.clearRecordLoadTimeout();
    this.clearRecordTypeUpdateTimer();
    this.recordData = null;
    this.recordDataContainer.recordData = null;
    this.mediaRecordData = null;
    this.beforeMealImage = null;
    this.afterMealImage = null;
    this.showRsstChart = false;
    this.showMwstView = false;
    this.showMonitoringView = false;
    this.showWaveCard = false;
    this.pendingWaveInit = false;
    this.vsMediaFallbackData = null;
    this.selectedEvent = null;
    this.parsedMeasurement = null;
    this.events = [];
    this.resetNeckAngleChartBindings();
    this.stopEncodingPoll();
    this.playerController.destroyPlayer();
    // Popup visibility is NOT reset here — popup persists across record navigation.
    // It will be shown/hidden when the new record's type is determined.
    this.scheduleRecordTypeUpdate(state.recordType ?? -1);
  }

  /**
   * ストリーミング URL から SAS クエリ・ハッシュを除いた「ソース同一性キー」を返す。
   * 再取得でトークンだけ変わった URL を「同じ動画」と判定し、成功済みプレイヤーの
   * 不要な作り直し（DRMキー解放→404 ループ）を防ぐための比較に使う。
   */
  private streamingSourceKey(url: string | null | undefined): string {
    if (!url) return '';
    const q = url.indexOf('?');
    const h = url.indexOf('#');
    let end = url.length;
    if (q >= 0) end = Math.min(end, q);
    if (h >= 0) end = Math.min(end, h);
    return url.slice(0, end);
  }

  /** ログ用に URL を短縮（.ism 以降のパス末尾だけ。クエリは「?…」に省略）。 */
  private shortStreamingUrl(url: string | null | undefined): string {
    if (!url) return '(none)';
    const path = this.streamingSourceKey(url);
    const hasQuery = url.length > path.length;
    const m = path.match(/\/([^/]+\.ism[^/]*)$/);
    const base = m ? m[1] : path.slice(-48);
    return hasQuery ? `${base}?…` : base;
  }

  private handleMediaRecordData(media: any): void {
    if (this.destroyed || !media) {
      this.mediaRecordData = null;
      this.lastHandledMediaRef = null;
      return;
    }

    // 同一オブジェクトの重複 emit はスキップ（初期化時の冗長処理・再生成チャーンを抑える）。
    if (media === this.lastHandledMediaRef) {
      return;
    }
    this.lastHandledMediaRef = media;

    const status = media?.data?.mediaStatus;
    const normalizedStatus = this.normalizeMediaStatus(status);
    // adjustStreamingUrlForBrowser は mediaRecordData を直接書き換えるため、
    // 先に新しい URL を確定させてから既存プレイヤーとの比較を行う。
    adjustStreamingUrlForBrowser({
      mediaRecordData: media,
      platformId: this.platformId,
    });

    const newUrl = media?.data?.storage?.streamingURL;
    const currentUrl = this.playerController.getCurrentStreamingUrl();
    // URL 完全一致ではなく SAS クエリを除いた manifest パスで一致判定する。
    // 再取得でトークンだけ変わっても成功済みプレイヤーを壊さない（再生成ループ対策）。
    const sameSource = this.streamingSourceKey(currentUrl) === this.streamingSourceKey(newUrl);
    const playerAlreadyInitialized =
      normalizedStatus === "encoded" &&
      !!this.playerController.getMkplayerInstance() &&
      sameSource;

    // メディア再取得（ポーリング等）が成功済みプレイヤーの再生成を誘発していないかの切り分け用。
    console.info('[player-controller][flow] handleMediaRecordData', {
      status: normalizedStatus,
      playerAlreadyInitialized,
      hasInstance: !!this.playerController.getMkplayerInstance(),
      sameSource,
      urlExactSame: currentUrl === newUrl,
      curUrl: this.shortStreamingUrl(currentUrl),
      newUrl: this.shortStreamingUrl(newUrl),
    });

    this.mediaRecordData = media;

    // 終端ステータス（Encoded / Failed 等）ではポーリングを停止
    const terminalStatuses = ["encoded", "failed", "error", "canceled", "cancelled", "deleted"];
    if (terminalStatuses.includes(normalizedStatus)) {
      this.stopEncodingPoll();
    }

    if (normalizedStatus === "encoded") {
      if (playerAlreadyInitialized) {
        return;
      }
      // Encoded 検知時は PlayerController が loading を維持し shimmer を表示。
      // canplay 到達で ready に遷移する。
    } else {
      // エンコード中/アップロード中は動画プレイヤーを停止
      // （vsMediaFallbackData で先行初期化されていた場合に備える）。
      //
      // ただし、同じ動画(sameSource)で既にプレイヤーが動作中(loading/ready/retrying)なら破棄しない。
      // バックエンドが encoded→encoding にステータスを逆戻りさせるケースで、再生中の動画を壊して
      // 再生成→manifest 404 ループに陥るのを防ぐ（status のバタつきに対する保険）。
      const sourceActive = sameSource && this.playerController.isSessionActive();
      if (!sourceActive) {
        this.playerController.destroyPlayer();
      } else {
        console.info('[player-controller][flow] handleMediaRecordData: keep player (status regressed but same source active)', {
          status: normalizedStatus,
        });
      }
    }
    // 音声・動画を問わず波形は独立して初期化する
    setTimeout(() => {
      this.requestWaveInitIfNeeded();
    }, 0);
  }

  onVideoHostReady(hostElement: HTMLElement): void {
    if (this.destroyed) {
      return;
    }
    const mediaSource = this.mediaRecordData ?? this.vsMediaFallbackData;
    const streamingUrl = mediaSource?.data?.storage?.streamingURL;
    if (!streamingUrl) {
      return;
    }

    // 同じソース（SASクエリを除く manifest パス）で既に初期化済み or ロード中ならスキップ。
    // URL 完全一致だと再取得でトークンだけ変わった際に成功済みプレイヤーを壊して作り直して
    // しまう（=DRMキー解放→404 ループの起点）。安定キーで比較してそれを防ぐ。
    const currentUrl = this.playerController.getCurrentStreamingUrl();
    const state = this.playerController.state();
    const sameSource = this.streamingSourceKey(currentUrl) === this.streamingSourceKey(streamingUrl);
    if (
      sameSource &&
      (state === 'loading' || state === 'ready' || state === 'retrying')
    ) {
      console.info('[player-controller][flow] onVideoHostReady: skip (same source + active)', {
        state,
        urlExactSame: currentUrl === streamingUrl,
      });
      return;
    }

    // PlayerController に初期化を委譲（レコード切替時はフラッシュ防止で replacePlayer）
    const isReplacing = state !== 'idle';
    // 再生成の原因切り分け用ログ（成功済みプレイヤーが作り直される=DRMキー解放ループの起点を特定する）。
    console.info('[player-controller][flow] onVideoHostReady: (re)create', {
      action: isReplacing ? 'replacePlayer' : 'createPlayer',
      state,
      sameSource,
      curUrl: this.shortStreamingUrl(currentUrl),
      newUrl: this.shortStreamingUrl(streamingUrl),
    });
    if (isReplacing) {
      this.playerController.replacePlayer(hostElement, mediaSource);
    } else {
      this.playerController.createPlayer(hostElement, mediaSource);
    }
    this.syncPlaybackRateFromVideo();
  }

  onWaveHostFromVideo(component: MeasuredDataWaveCardComponent): void {
    this.waveCardComponent = component;
    // waveは動画とは独立して表示するため、ホスト準備時点で初期化
    this.requestWaveInitIfNeeded();
    // waveContainer が ViewChild 初期化前で null の場合、DOM安定後に再試行
    if (
      this.pendingWaveInit &&
      !this.isWaveContainerReady(this.waveCardComponent?.waveContainer)
    ) {
      this.retryWaveInitUntilReady();
    }
  }

  private retryWaveInitUntilReady(): void {
    if (
      this.waveInitRetryTimer ||
      !this.pendingWaveInit ||
      this.destroyed ||
      !this.showWaveCard
    ) {
      return;
    }

    if (this.waveInitRetryAttempt > this.waveInitRetryMaxAttempts) {
      const container = this.waveCardComponent?.waveContainer;
      console.warn("[WaveDebug] retryWaveInitUntilReady exhausted", {
        attempts: this.waveInitRetryAttempt,
        isConnected: !!container?.isConnected,
        width: this.getWaveContainerWidth(container),
      });
      this.clearWaveInitRetryTimer();
      return;
    }

    const attempt = this.waveInitRetryAttempt;
    const delay = attempt === 0 ? 0 : attempt < 10 ? 16 : 50;
    this.waveInitRetryTimer = setTimeout(() => {
      this.waveInitRetryTimer = null;
      if (!this.pendingWaveInit || this.destroyed || !this.showWaveCard) {
        return;
      }
      this.waveInitRetryAttempt = attempt + 1;
      this.requestWaveInitIfNeeded();
      if (this.pendingWaveInit) {
        this.retryWaveInitUntilReady();
      }
    }, delay);
  }

  onVideoPlayToggle(): void {
    this.clearEventSeekStopTimer();
    // WAV-master(VS): 動画カードの再生ボタンも WAV(master) を起点にする。
    if (this.isVsWaveMaster()) {
      this.playPause();
      return;
    }
    const snapPlaying = this.playerController.snapshot().playing;
    if (snapPlaying) {
      this.cancelPendingSeek(); // stopRequested=true をセットする
    }
    this.playerController.togglePlay();
  }

  onVideoSeekTo(seconds: number): void {
    this.clearEventSeekStopTimer();
    // WAV-master(VS): 動画カードのスクラブは video 時刻。wave 時刻へ変換して WAV(master) をシーク。
    if (this.isVsWaveMaster()) {
      const w = this.avSync.window();
      const waveTime = w.videoOffsetSec + Math.max(0, seconds);
      this.waveService.seekToTime(waveTime, this.currentStartOffset);
      return;
    }
    this.playerController.seek(seconds);
  }

  onVideoRateChange(rate: number): void {
    this.playerController.setPlaybackRate(rate);
    this.waveService.setPlaybackRate(rate);
  }

  onVideoMuteToggle(): void {
    // WAV-master(VS): 動画は恒久ミュート。聞こえる音は WAV なので WAV 側をトグルする。
    if (this.isVsWaveMaster()) {
      const ws: any = this.getActiveWaveSurfer();
      try {
        const next = !(ws?.getMuted?.() ?? false);
        ws?.setMuted?.(next);
        this.wavMuted.set(next);
      } catch {
        /* ignore */
      }
      this.cdr.markForCheck();
      return;
    }
    this.playerController.toggleMute();
  }

  ngOnInit() {
    this.common.parametersUpdated
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((noData) => {
      this.selectedSection = this.common.getSelectedSection();
      this.selectedUserId = this.common.getSelectedUserId();

      this.selectedUserWithUserData = this.common.getSelectedUserWithUserData();

      if (this.selectedSection != null) {
        const isGostRoute = this.router.url.includes("/gost/");
        const recordListLink = isGostRoute
          ? [
              "/home",
              this.selectedSection.sectionId,
              "users",
              this.selectedUserId,
              "gost",
              "records",
            ]
          : [
              "/home",
              this.selectedSection.sectionId,
              "users",
              this.selectedUserId,
              "records",
            ];
        this.breadcrumbs = [
          {
            label: "",
            icon: "home",
            link: ["/home", this.selectedSection.sectionId],
          },
          {
            label: this.i18n.breadcrumb.userList,
            icon: "",
            link: ["/home", this.selectedSection.sectionId, "users"],
          },
          {
            label: this.i18n.breadcrumb.recordList,
            icon: "",
            link: recordListLink,
          },
          { label: this.i18n.breadcrumb.data },
        ];
      }
      this.cdr.markForCheck();
    });

    // route.params.subscribe への Subscribe は this.common.parametersUpdated への Subscribe よりあとに行うこと
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
      const userId = params["userId"];
      this.common.setSelectedUserId(userId);

      let recordId = params["recordId"]; // todo letからconstに戻す
      if (recordId === "new_gost") {
        this.common.prepareUserGostList(userId, false, true);
      }

      this.getRecord(recordId);
    });
  }

  getEventTypeLabel(type: string): string {
    try {
      const { translateEventType } = require("./measured-data-details-labels");
      return translateEventType(type);
    } catch {
      return type;
    }
  }

  ngAfterViewInit() {
    requestAnimationFrame(() => {});
    this.route.fragment.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((fragment) => {
      if (fragment) {
        this.popupWindowState.setCurrentFragment(fragment);
        this.popupWindowState.setExpanded(true);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearWaveInitRetryTimer();
    this.waveReadyPending = false;
    this.clearWaveReadyTimeout();
    this.clearRecordLoadTimeout();
    this.clearRecordTypeUpdateTimer();
    this.stopEncodingPoll();
    this.clearEventSeekStopTimer();
    this.vmSubscription.unsubscribe();
    this.waveService.destroy();
    this.waveTimeline.reset();
    this.resetNeckAngleChartBindings();
    // Popup is managed by UserDetailLayoutComponent — do NOT hide/destroy here
    // PlayerController は @Injectable の ngOnDestroy で自動破棄される
  }

  private createHiddenRegionParams() {
    const surfaceColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--gokuri-surface').trim() || '#ffffff';
    return {
      id: "-1",
      start: 0,
      end: this.timelineOffsetReference,
      drag: false,
      resize: false,
      showTooltip: false,
      preventContextMenu: false,
      color: surfaceColor,
    };
  }

  private shouldDisplayWaveCard(
    record: RecordData | null,
    recordType: number,
  ): boolean {
    if (!record) {
      return false;
    }
    if (record.id === "new_gost") {
      return false;
    }
    if (recordType === 7 && this.hasZeroDuration(record.data)) {
      return false;
    }
    if (
      recordType === 7 &&
      !this.hasStorageBlobResource(
        record.data?.storage?.audio,
        record.data?.storage?.audioSize ?? record.data?.audioSize,
      )
    ) {
      return false;
    }
    return recordType !== -1;
  }

  async initWaveSurfer(): Promise<void> {
    let audioUrl: string;
    try {
      if (this.waveInitInProgress) {
        return;
      }
      this.waveInitInProgress = true;
      this.waveInitRequested = true;
      this.waveReadyPending = false;
      this.clearWaveReadyTimeout();
      audioUrl = await this.resourceCache.resolveUrl(
        this.recordData?.data?.storage?.audio,
      );
    } catch (e: any) {
      this.waveInitInProgress = false;
      this.waveInitRequested = false;
      this.pendingWaveInit = false;
      this.clearWaveInitRetryTimer();
      this.recordLoadingSuccess = false;
      this.waveErrorMessage = this.getWaveErrorMessage(e);
      console.warn("[WaveDebug] resolveUrl failed", e);
      return;
    }

    if (!this.showWaveCard) {
      console.warn("[WaveDebug] showWaveCard=false, destroying wave");
      this.waveService.destroy();
      this.waveInitInProgress = false;
      this.waveInitRequested = false;
      this.pendingWaveInit = false;
      this.clearWaveInitRetryTimer();
      this.waveReadyPending = false;
      this.clearWaveReadyTimeout();
      return;
    }

    const container = this.waveCardComponent?.waveContainer;
    if (!this.isWaveContainerReady(container)) {
      console.warn("[WaveDebug] waveContainer is not render-ready", {
        hasContainer: !!container,
        width: this.getWaveContainerWidth(this.waveCardComponent?.waveContainer),
      });
      this.waveInitInProgress = false;
      this.waveInitRequested = false;
      this.waveReadyPending = false;
      this.clearWaveReadyTimeout();
      this.pendingWaveInit = true;
      this.retryWaveInitUntilReady();
      return;
    }

    // ローディングUI初期化（全プロトコルでストリーミングDLの進捗を表示）
    this.waveProcessing = false;
    this.waveDownloading = false;
    this.waveDownloadPercent = null;
    this.waveLoadingPercent = 0;

    const callbackSession = ++this.waveCallbackSession;
    // AvSync: VS で wave > video の場合、decode 側で trim せず raw 波形を保持する。
    // 末尾の余剰は visibleEndSec で UI clamp する（後段の effect で更新）。
    const isVSRecord = this.recordData?.data?.recordType === "VS";
    const initVideoDurationSec = isVSRecord
      ? null
      : this.videoDuration > 0
        ? this.videoDuration
        : null;
    const initialized = this.waveService.initialize({
      container,
      audioUrl,
      recordData: this.recordData,
      videoDurationSec: initVideoDurationSec,
      colors: {
        waveColor: this.waveColor,
        progressColor: this.progressColor,
        cursorColor: this.cursorColor,
      },
      callbacks: {
        onReady: () =>
          this.runWaveCallback(() => this.onWaveReady(), {
            defer: true,
            session: callbackSession,
          }),
        onPlay: () =>
          this.runWaveCallback(() => this.onWavePlay(), {
            session: callbackSession,
          }),
        onPause: () =>
          this.runWaveCallback(() => this.onWavePause(), {
            session: callbackSession,
          }),
        onFinish: () =>
          this.runWaveCallback(() => this.onWaveFinish(), {
            session: callbackSession,
          }),
        onAudioProcess: () => this.onWaveAudioProcess(),
        onSeeking: (time) =>
          this.runWaveCallback(() => this.onWaveSeeking(time), {
            session: callbackSession,
          }),
        onError: (error) =>
          this.runWaveCallback(() => this.onWaveError(error), {
            defer: true,
            session: callbackSession,
          }),
        onLoading: (percent) =>
          this.runWaveCallback(() => this.onWaveLoading(percent), {
            session: callbackSession,
          }),
        onDownloadStart: () =>
          this.runWaveCallback(() => this.onWaveDownloadStart(), {
            session: callbackSession,
          }),
        onDownloadProgress: (p) =>
          this.runWaveCallback(() => this.onWaveDownloadProgress(p), {
            session: callbackSession,
          }),
        onDownloadDone: () =>
          this.runWaveCallback(() => this.onWaveDownloadDone(), {
            session: callbackSession,
          }),
        onProcessingStart: () =>
          this.runWaveCallback(() => this.onWaveProcessingStart(), {
            session: callbackSession,
          }),
        onProcessingDone: () =>
          this.runWaveCallback(() => this.onWaveProcessingDone(), {
            session: callbackSession,
          }),
      },
      events: this.events,
      regionService: this.regionService,
      waveTimeline: this.waveTimeline,
      hiddenRegionParams: this.createHiddenRegionParams(),
      timelineOffsetReference: this.timelineOffsetReference,
      currentStartOffset: this.currentStartOffset,
      wavDurationMs: this.recordData?.data?.wavDuration ?? null,
    });

    if (initialized) {
      this.waveInitialized = true;
      this.pendingWaveInit = false;
      this.clearWaveInitRetryTimer();
      this.startWaveReadyTimeout();
    }

    if (!initialized) {
      const latestContainer = this.waveCardComponent?.waveContainer ?? container;
      if (this.showWaveCard && !this.isWaveContainerReady(latestContainer)) {
        console.warn(
          "[WaveDebug] WaveService.initialize deferred; container became non-renderable",
          {
            hasContainer: !!latestContainer,
            width: this.getWaveContainerWidth(this.waveCardComponent?.waveContainer),
          },
        );
        this.waveInitInProgress = false;
        this.waveInitRequested = false;
        this.waveReadyPending = false;
        this.clearWaveReadyTimeout();
        this.pendingWaveInit = true;
        this.retryWaveInitUntilReady();
        return;
      }
      this.recordLoadingSuccess = false;
      this.waveErrorMessage = this.i18n.waveError.audioNotFound;
      this.waveInitInProgress = false;
      this.waveInitRequested = false;
      this.pendingWaveInit = false;
      this.clearWaveInitRetryTimer();
      this.waveReadyPending = false;
      this.clearWaveReadyTimeout();
      return;
    }
    this.waveInitInProgress = false;
  }

  /**
   * 動画の現在位置に応じて波形のカーソルだけを描画更新する。
   * WebAudio/WebElement の再生・バッファは動かさない。
   */
  private updateWaveCursorFromVideo(
    force: boolean = false,
    overrideCurrentTime?: number,
  ): void {
    const wavesurfer: any = this.getActiveWaveSurfer();
    if (!wavesurfer) {
      return;
    }
    const duration = Number(wavesurfer.getDuration?.() ?? 0);
    const current =
      overrideCurrentTime ??
      Number(this.playerController.getMkplayerInstance()?.getCurrentTime?.() ?? 0);
    if (
      !Number.isFinite(duration) ||
      duration <= 0 ||
      !Number.isFinite(current)
    ) {
      return;
    }
    // WAV-master では本メソッドは非 VS / 非同期窓のフォールバック用。動画時刻=波形時刻の
    // identity 扱い（VS+動画のカーソルは WAV 自身が master なので本メソッドを通らない）。
    const waveTime = current;
    const target = Math.min(Math.max(waveTime / duration, 0), 1);
    const currentPos = wavesurfer.getCurrentTime
      ? wavesurfer.getCurrentTime() / duration
      : 0;
    if (!force && Math.abs(target - currentPos) < 0.0001) {
      return;
    }

    // WaveSurfer v7: renderer.renderProgress() は media.currentTime を変えないため
    // HTML seeking イベントが発火せずフィードバックループが発生しない。
    const renderer = (wavesurfer as any).renderer;
    if (renderer?.renderProgress) {
      try {
        renderer.renderProgress(target, wavesurfer.isPlaying?.() ?? false);
        return;
      } catch {
        // ignore
      }
    }

    // WaveSurfer v6: drawer.progress() も視覚のみ更新
    const drawer = (wavesurfer as any).drawer;
    if (drawer?.progress) {
      try {
        drawer.progress(target);
        return;
      } catch {
        // ignore
      }
    }

    // 最終フォールバック: seekTo は media.currentTime を変え seeking イベントが非同期発火するため
    // 可能な限り使用しない（scyncedSeek でループ防止）
    if (this.scyncedSeek) {
      return;
    }
    this.scyncedSeek = true;
    try {
      wavesurfer.seekTo(target);
    } finally {
      this.scyncedSeek = false;
    }
  }

  /**
   * 波形/動画の両方を確実に停止して先頭へ戻す共通処理。
   * playback finished 時など競合状態でも確実に止めるため、余計な例外は握りつぶさずログ出力する。
   */

  private cancelPendingSeek(): void {
    if (this.seekDebounceTimer) {
      clearTimeout(this.seekDebounceTimer);
      this.seekDebounceTimer = null;
    }
    if (this.seekInProgressTimer) {
      clearTimeout(this.seekInProgressTimer);
      this.seekInProgressTimer = null;
    }
    this.seekInProgress = false;
    this.wasPlayingBeforeSeek = false;
    this.stopRequested = true;
  }

  private stopAllPlayback(
    reason: string,
    callHandleFinished: boolean = false,
  ): void {
    const wavesurfer = this.getActiveWaveSurfer();
    this.clearEventSeekStopTimer();
    this.cancelPendingSeek();
    this.stopRequested = false;
    this.ignoreNextPause = false;

    // 再生終了として表示側に反映
    if (callHandleFinished || reason.includes("finish")) {
      this.playbackEnded = true;
    }

    // WaveSurfer を一時停止（巻き戻さない）
    try {
      wavesurfer?.pause?.();
    } catch {
      // ignore
    }

    // 動画も一時停止のみ（PlayerController 経由）
    this.playerController.pause();

    this.playing = false;
    this.videoPlaying = false;
    this.ref.markForCheck();
    // 終了直後のゴーストイベントをしばらく無視
    this.finishingReset = true;
    setTimeout(() => {
      this.finishingReset = false;
    }, 50);
  }

  private clearEventSeekStopTimer(): void {
    if (this.eventSeekStopTimer) {
      clearTimeout(this.eventSeekStopTimer);
      this.eventSeekStopTimer = null;
    }
  }

  findClosestNeckAngleValues(overrideCurrentTimeSec?: number) {
    const wavesurfer = this.getActiveWaveSurfer();
    if (!wavesurfer) {
      return;
    }
    // チャートインスタンスが未初期化の場合はオプション更新をスキップ
    // ngx-echartsの非同期createChart完了前にオプションが変わると
    // "already initialized on the dom" 警告が発生するため
    if (!this.neckAngleScatterChartInstance || !this.currentNeckAngleScatterChartInstance) {
      return;
    }
    const currentTimeSec = Number.isFinite(overrideCurrentTimeSec)
      ? Number(overrideCurrentTimeSec)
      : Number(wavesurfer.getCurrentTime?.() ?? 0);
    if (!Number.isFinite(currentTimeSec)) {
      return;
    }
    const currentTimeMs = currentTimeSec * 1000;

    const currentScatter = this.neckAngleService.findClosestScatter(
      this.neckAngleScatterData,
      currentTimeMs,
    );
    const currentOrientation = this.neckAngleService.findClosestOrientation(
      this.neckOrientationData,
      currentTimeMs,
    );

    let changed = false;

    if (currentScatter && this.currentNeckAngleScatterData !== currentScatter) {
      this.currentNeckAngleScatterData = currentScatter;
      this.currentChartOption = this.neckAngleService.createCurrentChartOption([
        currentScatter,
      ]);
      changed = true;
    }

    if (
      currentOrientation &&
      this.currentNeckOrientation !== currentOrientation
    ) {
      this.currentNeckOrientation = currentOrientation;
      changed = true;
    }

    if (changed) {
      this.cdr.markForCheck();
    }
  }

  private async loadCsvData(): Promise<void> {
    const currentData = this.recordData?.data;
    if (this.isGostV2Record(currentData) && this.hasZeroDuration(currentData)) {
      this.parsedMeasurement = null;
      this.cdr.markForCheck();
      return;
    }
    const storage = currentData?.storage;
    const csvUrl = storage?.data;
    const csvSize = storage?.dataSize ?? currentData?.dataSize;
    if (!this.hasStorageBlobResource(csvUrl, csvSize)) {
      this.avSync.setVideoRange(null);
      this.applyAvSyncWindowToWave();
      this.parsedMeasurement = null;
      this.cdr.markForCheck();
      return;
    }
    try {
      const text = await this.resourceCache.fetchCsvText(csvUrl);
      const results = await this.parseCsvText(text);
      this.setCsvData(results);
    } catch (error) {
      this.hideWaveTimelineOnNotFound(error);
      if (!this.isNotFoundBlobError(error)) {
        console.warn(
          "[INFO] measured-data-details.component.ts > loadCsvData() csv error",
          error,
        );
      }
      this.avSync.setVideoRange(null);
      this.applyAvSyncWindowToWave();
      this.parsedMeasurement = null;
      this.cdr.markForCheck();
    }
  }

  private parseCsvText(text: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.papa.parse(text, {
        download: false,
        complete: (results) => resolve(results),
        error: (err) => reject(err),
      });
    });
  }

  setCsvData = (results) => {
    this.events = [];
    this.regionService.updateEvents(this.events);
    this.selectedEvent = null;
    this.pieChartData = [];
    this.pieChartColor = [];
    this.neckAngleScatterData = [];
    this.currentNeckAngleScatterData = null;
    this.neckOrientationData = [];
    this.currentNeckOrientation = null;
    this.avSync.setVideoRange(null);
    processCsvResults(results, this.buildCsvProcessingContext());
    this.waveTimeline.applyDefaultOffsetState();
    this.applyAvSyncWindowToWave();
    this.applyNeckAngleOptionsFromData();
    this.currentNeckOrientation = this.neckOrientationData.length
      ? this.neckOrientationData[0]
      : null;

    // Build normalized measurement for protocol components
    const postureData: PostureDataPoint[] = this.neckOrientationData.map(n => ({
      timeMs: n.timeMs,
      roll: n.roll,
      pitch: n.pitch,
      yaw: n.yaw,
    }));
    // OnPush 子コンポーネントが検知できるよう配列参照を更新
    this.pieChartData = [...this.pieChartData];
    this.pieChartColor = [...this.pieChartColor];

    this.parsedMeasurement = buildParsedMeasurement(
      this.events,
      this.pieChartData,
      this.pieChartColor,
      postureData,
      this.currentStartOffset,
      this.recordData,
    );
    this.cdr.markForCheck();
  };

  private buildCsvProcessingContext(): CsvProcessingContext {
    return {
      recordData: this.recordData,
      events: this.events,
      pieChartData: this.pieChartData,
      pieChartColor: this.pieChartColor,
      neckAngleScatterData: this.neckAngleScatterData,
      neckOrientationData: this.neckOrientationData,
      addRegionWithMouseEvent: (params) =>
        this.regionService.addRegionWithMouseEvent(params, (selected) => {
          this.ngZone.run(() => {
            this.selectedEvent = selected;
            this.cdr.markForCheck();
          });
        }),
      addMarker: (time, color, label?, position?) =>
        this.regionService.addMarker(time, color, label, position),
      setCurrentStartOffset: (offset) =>
        this.waveTimeline.updateStartOffset(offset),
      setTimelineOffset: () => this.waveTimeline.refreshTimelineOffset(),
      setVsVideoRange: (range) => this.avSync.setVideoRange(range),
    };
  }

  playPause() {
    this.clearEventSeekStopTimer();
    // WAV-master(VS+動画): WAV(wavesurfer) を起点に再生/停止。ミュート動画は
    // onWavePlay/onWavePause 経由で従属する。
    if (this.isVsWaveMaster()) {
      if (this.playing) {
        this.cancelPendingSeek();
        this.playing = this.waveService.togglePlay(
          this.currentStartOffset,
          this.playing,
        );
        return;
      }
      // 終了後（または終端位置）からの再生は「はじめから」再生する（replay）。
      if (this.playbackEnded || this.isWaveAtEnd()) {
        this.playbackEnded = false;
        this.waveService.seek(0, this.currentStartOffset);
      }
      this.playing = this.waveService.togglePlay(
        this.currentStartOffset,
        this.playing,
      );
      return;
    }
    // メディア（波形なし動画）等は従来どおり動画側を起点にする。
    // getMkplayerInstance() は retrying 中 null を返すため guard に使えない。
    // isSessionActive() で state ベースに判定し、retrying 中も動画パスを維持する。
    if (this.playerController.isSessionActive()) {
      const snapPlaying = this.playerController.snapshot().playing;
      if (snapPlaying) {
        this.cancelPendingSeek(); // stopRequested=true をセットする
      }
      this.playerController.togglePlay();
      return;
    }
    this.waveService.togglePlay(this.currentStartOffset, this.playing);
  }

  zoomPlus() {
    this.zoomValue = this.waveService.applyZoom(this.zoomStep, this.zoomValue);
    this.waveService.refreshTimelineOffset();
  }

  zoomMinus() {
    this.zoomValue = this.waveService.applyZoom(-this.zoomStep, this.zoomValue);
    this.waveService.refreshTimelineOffset();
  }

  zoomReset() {
    // 波形のズームと表示位置のみ初期化（RSSTドーナツ等は触らない）
    // 初期状態と同じ hiddenRegion / タイムライン補正を再適用する
    this.waveTimeline.applyDefaultOffsetState();
    this.currentStartOffset = this.waveTimeline.getCurrentStartOffset();
    // タイムラインプラグイン側にもオフセットを再適用
    this.waveTimeline.updateStartOffset(this.currentStartOffset);
    this.zoomValue = this.waveService.resetZoom(
      this.zoomValue,
      this.currentStartOffset,
    );
    this.waveService.resetView(this.currentStartOffset);
    this.waveService.refreshTimelineOffset();
  }

  offsetChanged(offsetEnabled: boolean) {
    this.waveTimeline.handleOffsetChange(offsetEnabled);
  }

  seekTo(position: number) {
    this.clearEventSeekStopTimer();
    this.waveService.seek(position, this.currentStartOffset);
  }

  seekToEvent(event: SeekEvent): void {
    const startTime = Number(event?.startTime);
    if (!Number.isFinite(startTime)) {
      return;
    }

    const durationMs = Number(event?.durationMs);
    const safeDurationMs = Number.isFinite(durationMs)
      ? Math.max(0, Math.round(durationMs))
      : 0;

    this.clearEventSeekStopTimer();
    this.playbackEnded = false;

    // WAV-master: タグ(event.startTime)は raw wave 時刻軸。波形があれば変換せず
    // WAV(master)をそのままシーク+再生する → タグ・波形・音が完全一致。
    // ミュート動画は onWaveSeeking/onWavePlay 経由で従属する。
    const wavesurfer = this.getActiveWaveSurfer();
    if (wavesurfer) {
      // メディア時間基準の区間再生に委譲する。停止は wave service が getCurrentTime
      // ベースで行うため、モバイルのバッファ待ちで再生開始が遅れても区間がフルで鳴る。
      // play()/pause() の状態（playing・AvSync動画追従）は onWavePlay/onWavePause 経由で同期。
      this.waveService.playRange(startTime, safeDurationMs / 1000, this.currentStartOffset);
      this.playing = true;
      this.cdr.markForCheck();
      return;
    }

    if (this.playerController.getMkplayerInstance()) {
      // 波形なしのメディア専用フォールバック: 動画を直接駆動する。
      this.playerController.seek(startTime);
      this.playerController.play();
      this.playing = true;
      this.videoPlaying = true;
    } else {
      return;
    }

    this.eventSeekStopTimer = setTimeout(() => {
      this.eventSeekStopTimer = null;
      this.playerController.pause();
      this.playing = false;
      this.videoPlaying = false;
      this.cdr.markForCheck();
    }, safeDurationMs);

    this.cdr.markForCheck();
  }

  async getRecord(recordId: string) {
    this.error = null;
    this.recordId = recordId;
    this.popupWindowState.setCurrentRecordId(recordId ?? '');

    if (!this.recordId) {
      this.clearRecordLoadTimeout();
      this.scheduleRecordTypeUpdate(-2);
      this.isLoadingRecord = false;
      this.recordLoadingSuccess = false;
      this.error = { message: this.i18n.recordError.notFoundOrDeleted };
      return;
    }

    this.startRecordLoadTimeout();
    try {
      await this.viewModel.loadRecord(this.recordId);
      this.error = null;
    } catch (error) {
      this.recordLoadingSuccess = false;
      this.error = { message: this.getRecordErrorMessage(error) };
      this.cdr.markForCheck();
    } finally {
      this.clearRecordLoadTimeout();
    }
  }

  private scheduleRecordTypeUpdate(value: number): void {
    if (this.recordtype === value) {
      this.updatePopupVisibility(value);
      return;
    }
    this.clearRecordTypeUpdateTimer();
    this.ngZone.runOutsideAngular(() => {
      this.recordTypeUpdateTimer = setTimeout(() => {
        this.recordTypeUpdateTimer = null;
        if (this.destroyed) {
          return;
        }
        this.ngZone.run(() => {
          if (this.destroyed) {
            return;
          }
          this.recordtype = value;
          this.updatePopupVisibility(value);
          this.cdr.markForCheck();
          this.ngZone.runOutsideAngular(() => {
            setTimeout(() => {
              if (this.destroyed) {
                return;
              }
              this.ngZone.run(() => {
                if (!this.destroyed) {
                  this.requestWaveInitIfNeeded();
                }
              });
            });
          });
        });
      });
    });
  }

  private updatePopupVisibility(recordType: number): void {
    // Only show — never hide here. Hiding is handled by UserDetailLayoutComponent
    // when navigating away from record detail routes.
    if (recordType >= 0) {
      this.popupWindowState.show();
    }
  }

  private resetNeckAngleChartBindings(): void {
    if (this.neckAngleScatterChartInstance) {
      try {
        if (!this.neckAngleScatterChartInstance.isDisposed()) {
          this.neckAngleScatterChartInstance.off("datazoom", this.onBaseDataZoom);
        }
      } catch {
        // ignore
      }
      this.neckAngleScatterChartInstance = null;
    }
    if (this.currentNeckAngleScatterChartInstance) {
      try {
        if (!this.currentNeckAngleScatterChartInstance.isDisposed()) {
          this.currentNeckAngleScatterChartInstance.off(
            "datazoom",
            this.onCurrentDataZoom,
          );
        }
      } catch {
        // ignore
      }
      this.currentNeckAngleScatterChartInstance = null;
    }
  }

  private applyNeckAngleOptionsFromData(): void {
    if (
      !this.neckAngleScatterChartInstance ||
      !this.currentNeckAngleScatterChartInstance
    ) {
      this.neckAngleOptionUpdatePending = true;
      return;
    }
    this.chartOption = this.neckAngleService.createChartOption(
      this.neckAngleScatterData,
    );
    const currentData = this.currentNeckAngleScatterData
      ? [this.currentNeckAngleScatterData]
      : [];
    this.currentChartOption =
      this.neckAngleService.createCurrentChartOption(currentData);
    this.neckAngleOptionUpdatePending = false;
  }

  private flushPendingNeckAngleOptions(): void {
    if (!this.neckAngleOptionUpdatePending) {
      return;
    }
    this.applyNeckAngleOptionsFromData();
    this.cdr.markForCheck();
  }

  private hasChartDomSize(chart: echarts.ECharts | null): boolean {
    const dom = chart?.getDom?.() as HTMLElement | undefined;
    if (!dom) {
      return false;
    }
    return dom.clientWidth > 0 && dom.clientHeight > 0;
  }

  onNeckAngleChartInit(chart: echarts.ECharts): void {
    if (this.destroyed) {
      return;
    }
    if (this.neckAngleScatterChartInstance && this.neckAngleScatterChartInstance !== chart) {
      try {
        this.neckAngleScatterChartInstance.off("datazoom", this.onBaseDataZoom);
      } catch {
        // ignore
      }
    }
    this.neckAngleScatterChartInstance = chart;
    chart.off("datazoom", this.onBaseDataZoom);
    chart.on("datazoom", this.onBaseDataZoom);
    this.flushPendingNeckAngleOptions();
  }

  onCurrentNeckAngleChartInit(chart: echarts.ECharts): void {
    if (this.destroyed) {
      return;
    }
    if (
      this.currentNeckAngleScatterChartInstance &&
      this.currentNeckAngleScatterChartInstance !== chart
    ) {
      try {
        this.currentNeckAngleScatterChartInstance.off(
          "datazoom",
          this.onCurrentDataZoom,
        );
      } catch {
        // ignore
      }
    }
    this.currentNeckAngleScatterChartInstance = chart;
    chart.off("datazoom", this.onCurrentDataZoom);
    chart.on("datazoom", this.onCurrentDataZoom);
    this.flushPendingNeckAngleOptions();
    if (this.hasChartDomSize(this.currentNeckAngleScatterChartInstance)) {
      this.currentNeckAngleScatterChartInstance.resize();
    }
    if (this.hasChartDomSize(this.neckAngleScatterChartInstance)) {
      this.neckAngleScatterChartInstance.resize();
    }
  }

  private encodingPollCount = 0;

  private startEncodingPoll(mediaId: string): void {
    this.stopEncodingPoll();
    this.encodingPollCount = 0;
    this.consecutiveMediaLoadErrors = 0;
    this.scheduleNextEncodingPoll(mediaId);
  }

  /**
   * setTimeout チェーンで loadMedia を呼ぶ。
   * setInterval と異なりリクエスト完了を待ってから次をスケジュールするため重複しない。
   * 30分〜1時間の動画エンコードを想定し、段階的にバックオフする:
   *   最初の5回: 30秒間隔（2.5分）
   *   次の10回: 60秒間隔（10分）
   *   以降: 180秒間隔（上限なし、ページ離脱まで継続）
   */
  private getEncodingPollInterval(): number {
    if (this.encodingPollCount < 5) return 30_000;
    if (this.encodingPollCount < 15) return 60_000;
    return 180_000;
  }

  private scheduleNextEncodingPoll(mediaId: string): void {
    const intervalMs = this.getEncodingPollInterval();
    this.encodingPollTimer = setTimeout(async () => {
      if (this.destroyed) {
        return;
      }
      this.encodingPollCount++;
      try {
        await this.viewModel.loadMedia(mediaId);
      } catch {
        // loadMedia 内でエラーハンドリング済み
      }
      // getMedia が連続で失敗（500等）する場合はサーバ側の恒久エラーとみなし
      // ポーリングを打ち切る（エラー表示は mediaLoadFailed が担う）
      if (this.lastMediaError) {
        this.consecutiveMediaLoadErrors++;
        if (this.consecutiveMediaLoadErrors >= 3) {
          this.stopEncodingPoll();
          return;
        }
      } else {
        this.consecutiveMediaLoadErrors = 0;
      }
      // Encoded / 失敗ステータスで handleMediaRecordData が stopEncodingPoll を呼ぶので、
      // タイマーがクリアされていなければ次をスケジュール
      if (this.encodingPollTimer !== null && !this.destroyed) {
        this.scheduleNextEncodingPoll(mediaId);
      }
    }, intervalMs);
  }

  private stopEncodingPoll(): void {
    if (this.encodingPollTimer) {
      clearTimeout(this.encodingPollTimer);
      this.encodingPollTimer = null;
    }
  }

}
